import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CloseButtonIcon from '@/component-library/primitives/Icons/CloseButtonIcon/CloseButtonIcon';
import NoDataBoard from '@/component-library/widgets/NoDataBoard/NoDataBoard';
import { QuickFilter, QuickFilterCriteria } from '@/component-library/widgets/QuickFilter/QuickFilter';
import Table from '@/component-library/widgets/Table/Table';
import { Member } from '@/models/AudienceByWorkspace';

import { useAudienceModalTable } from './useAudienceModalTable';

interface ModalProps {
  isOpen: boolean;
  data: Member[] | undefined;
  onCancel: () => void;
}

const AudienceModal: React.FC<ModalProps> = ({ isOpen, data, onCancel }) => {
  const { columns } = useAudienceModalTable();
  const { t } = useTranslation();
  const [quickFilterCriteria, setQuickFilterCriteria] = useState<QuickFilterCriteria>({});

  const filteredData = useMemo(() => {
    if (!data || data.length === 0) return [];

    const getRiskLevelFromScore = (riskScore: number) => {
      if (riskScore >= 60) return 'high';
      if (riskScore >= 30) return 'medium';
      return 'low';
    };

    const getStatusFromRow = (row: Member) => {
      const { subscribed, active, stripeCreatedAt, createdAt, parsedChurnRiskData } = row;
      const subscriptionStatus = parsedChurnRiskData?.subscriptionStatus;
      const engagementLevel = parsedChurnRiskData?.engagementLevel;
      const riskScore = parsedChurnRiskData?.riskScore ?? 0;

      const isNewUser = (() => {
        const createdDate = stripeCreatedAt ? new Date(stripeCreatedAt * 1000) : new Date(createdAt);
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        return createdDate > sevenDaysAgo && riskScore < 30 && engagementLevel === 'low';
      })();

      if (isNewUser) return 'new_user';
      if (subscriptionStatus === 'active' && subscribed && active) return 'active';
      if (subscriptionStatus === 'cancelled') return 'cancelled';
      if (subscriptionStatus === 'past_due') return 'past_due';
      if (subscriptionStatus === 'expired') return 'past_customer';
      if (subscriptionStatus === 'trialing') return 'free_trial';
      return 'free';
    };

    return data.filter((row: Member) => {
      // Apply risk level filter
      if (quickFilterCriteria.riskLevel) {
        const score = row?.parsedChurnRiskData?.riskScore ?? 0;
        const level = getRiskLevelFromScore(score);
        if (level !== quickFilterCriteria.riskLevel) return false;
      }

      // Apply email domain filter
      if (quickFilterCriteria.emailDomain) {
        const email = row?.email || '';
        const domain = quickFilterCriteria.emailDomain.toLowerCase().replace('@', '');
        if (!email.toLowerCase().includes(`@${domain}`) && !email.toLowerCase().endsWith(`@${domain}`)) {
          return false;
        }
      }

      // Apply risk score range filter
      if (
        (quickFilterCriteria.riskScoreFrom !== null && quickFilterCriteria.riskScoreFrom !== undefined) ||
        (quickFilterCriteria.riskScoreTo !== null && quickFilterCriteria.riskScoreTo !== undefined)
      ) {
        const score = row?.parsedChurnRiskData?.riskScore ?? 0;
        if (
          quickFilterCriteria.riskScoreFrom !== null &&
          quickFilterCriteria.riskScoreFrom !== undefined &&
          score < quickFilterCriteria.riskScoreFrom
        )
          return false;
        if (
          quickFilterCriteria.riskScoreTo !== null &&
          quickFilterCriteria.riskScoreTo !== undefined &&
          score > quickFilterCriteria.riskScoreTo
        )
          return false;
      }

      // Apply status filter
      if (quickFilterCriteria.status) {
        const status = getStatusFromRow(row);
        if (status !== quickFilterCriteria.status) return false;
      }

      // Apply signal action type filter (check if user has signals that can be resolved with discount)
      if (quickFilterCriteria.signalActionType === 'discount') {
        // Check if user has recommended actions that include discount-related actions
        const recommendedActions = row?.recommendedActions || [];
        const parsedChurnRiskActions = row?.parsedChurnRiskData?.recommendedActions || [];
        const allActions = [...recommendedActions, ...parsedChurnRiskActions];

        const hasDiscountAction = allActions.some((action: string) => {
          const actionLower = action.toLowerCase();
          return actionLower.includes('discount') || actionLower.includes('promo') || actionLower.includes('offer');
        });

        if (!hasDiscountAction) return false;
      }

      return true;
    });
  }, [data, quickFilterCriteria]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-modalOpacityBG backdrop-blur-[10px]">
      <div className="bg-darkBlue min-w-[75rem] max-w-[90rem] min-h-[41rem] p-6 rounded-lg relative">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-white text-xl font-semibold">Users in Segment</h2>
          <div className="flex items-center gap-3">
            <QuickFilter
              onApply={(criteria) => {
                setQuickFilterCriteria(criteria);
              }}
              onClear={() => {
                setQuickFilterCriteria({});
              }}
              activeCriteria={quickFilterCriteria}
              presets={[
                {
                  id: 'high-risk-instagram',
                  name: 'High Risk + Instagram',
                  description: 'Users with HIGH RISK and @instagram.com email domain',
                  criteria: {
                    riskLevel: 'high',
                    emailDomain: 'instagram.com',
                  },
                },
                {
                  id: 'risk-score-60-85',
                  name: 'Risk Score 60-85%',
                  description: 'Users with risk score between 60% and 85%',
                  criteria: {
                    riskScoreFrom: 60,
                    riskScoreTo: 85,
                  },
                },
                {
                  id: 'discount-signals',
                  name: 'Discount Action Signals',
                  description: 'Users with signals that can be resolved with discount',
                  criteria: {
                    signalActionType: 'discount',
                  },
                },
                {
                  id: 'high-risk-active',
                  name: 'High Risk Active Users',
                  description: 'Active users with high churn risk',
                  criteria: {
                    riskLevel: 'high',
                    status: 'active',
                  },
                },
              ]}
            />
            <button type="button" onClick={onCancel} className="group">
              <span className="group-hover:[&>*_path]:stroke-gray-100">
                <CloseButtonIcon />
              </span>
            </button>
          </div>
        </div>
        {filteredData && filteredData.length > 0 ? (
          <Table columns={columns} data={filteredData ?? []} showRecordsPerPage={false} />
        ) : (
          <NoDataBoard description={t('SegmentOverview.modalDescription')} className="!h-[35rem]" />
        )}
      </div>
    </div>
  );
};

export default AudienceModal;
